import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import css from './QuickQuestionOptInModal.module.css';
import { BubbleTextInput } from '../../widgets/common/BubbleTextInput/BubbleTextInput';
import Hint from '../../components/Hint/Hint';
import { Button, Link } from '@mm/components';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { CloseOutlined } from '@ant-design/icons';
import { ModalInstanceProps } from '../interfaces';
import { colors } from '@mm/utils';
import { useSelector } from 'react-redux';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';

type Props = {
  className?: string;
  chatbotId: number;
  title?: string;
  error?: any;
  value?: string;
  onReject?: () => void;
  onSubmit?: (value: string) => Promise<void>;
} & ModalInstanceProps;

export const SMSNotActiveError = ({
  chatbotId,
  onClick,
}: {
  chatbotId: number;
  onClick: () => void;
}) => {
  return (
    <>
      Phone number is not activated.&nbsp;
      <Link
        onLinkClick={onClick}
        to={BotBuilderRoutes.settingsSmsActivate.getLink({
          chatbotId,
        })}
      >
        Activate phone number
      </Link>
    </>
  );
};

export const QuickQuestionOptInModal = ({
  className,
  chatbotId,
  title: titleValue = 'Send opt-in confirmation',
  error: errorValue = '',
  value = '',
  onReject = () => {},
  onSubmit = async () => {},
  closeModal,
}: Props) => {
  const smsActivated = useSelector(hasSMSNumberSelector);

  const [title, setTitle] = useState(titleValue);

  const [error, setError] = useState<any>(errorValue);
  const [inputValue, setInputValue] = useState(value);

  const trimmed = inputValue.toString().trim();
  const canSubmit = trimmed && trimmed !== value?.trim();

  const handleReject = async () => {
    closeModal();
    onReject();
  };

  const handleSubmit = async () => {
    if (!smsActivated) {
      setTitle('Phone number activation');
      setError(
        <SMSNotActiveError chatbotId={chatbotId} onClick={closeModal} />,
      );

      return;
    }

    try {
      await onSubmit(inputValue);
      closeModal();
    } catch (err) {
      setTitle(err);
      setError(err);
    }
  };

  const content = useMemo(
    () => (
      <>
        <div className={css.reminder}>
          <BubbleTextInput
            value={inputValue}
            editMode
            maxLength={160}
            emojiPickerColor={colors.gray}
            attributePickerColor={colors.gray}
            onChange={setInputValue}
            saveOnKeyboard={true}
          />
          <Hint className={css.hint}>
            Opt-in confirmation won’t be sent until the contact provides a valid
            phone number. See Phone Number Confirmation dialogue
          </Hint>
        </div>

        <div className={css.actions}>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            Save
          </Button>
          <Button onClick={handleReject} color="transparent">
            Cancel
          </Button>
        </div>
      </>
    ),
    [canSubmit, handleReject, handleSubmit],
  );

  return (
    <div className={classNames(className, css.container)}>
      <div className={classNames(css.modal, { [css.error]: error })}>
        <div className={css.modalContent}>
          <span className={css.close} onClick={handleReject}>
            <CloseOutlined />
          </span>

          <h1 className={css.title}>{title}</h1>

          {error ? <Hint className={css.hint}>{error}</Hint> : content}
        </div>
      </div>
    </div>
  );
};
